import { phishingSignsLabels } from '@/client/campaigns';
import { CampaignUserResultStatusesEnum } from '@/client/campaigns/types/campaign.type';
import { MyPhishingSimulationSummary } from '@/client/my-phishing';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useMyPhishingOverview } from '@/hooks/query';
import { usePhishingFollowUpEnabled } from '@/hooks/usePhishingFollowUpEnabled';
import CalendarIcon from '@/images/icons/calendar.svg';
import CampaignThumbnailPlaceholder from '@/images/placeholders/campaign-thumbnail-placeholder.svg';
import { LearnerLayout } from '@/pages/dashboards/learner/LearnerLayout';
import { LEARNER_CONTENT_FORBIDDEN_TAGS } from '@/pages/dashboards/learner/phishing/constants';
import { getStatusChipType } from '@/pages/dashboards/learner/phishing/SimulationDetailBanner';
import { LoadingPage } from '@/pages/LoadingPage';
import AppChip from '@/ui/chip/AppChip';
import { FormatDate } from '@/ui/date';
import { PhishingDashboardCard } from '@/ui/phishing-dashboard-card/PhishingDashboardCard';
import { FlexContainer } from '@/ui/styled-ui';
import { stripUnwantedHtmlTags } from '@/utils/helpers';
import {
  CircledCheckIcon,
  EmailSentIcon,
  StatClickIcon,
  StatSubmitIcon,
  StatWarningIcon,
} from '@/utils/helpers/icons.helper';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledBeigeContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background-color: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  padding: 50px;
  margin-top: 54px;
  display: flex;
  flex-direction: column;
  gap: 46px;

  @media screen and (max-width: 860px) {
    padding: var(--medium-padding);
    gap: var(--default-padding);
    margin-top: var(--default-padding);
  }
`;

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const StyledSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledSectionTitle = styled.h2`
  margin: 0;
  font-weight: 700;
  font-size: var(--heading-medium-font-size);
  line-height: var(--heading-medium-line-height);

  @media screen and (max-width: 480px) {
    font-size: var(--semi-big-font-size);
    line-height: var(--semi-big-line-height);
  }
`;

const StyledSectionDescription = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);
`;

const StyledStatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 17px;

  > div > div p {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
  }

  > div .description {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }

  @media screen and (max-width: 860px) {
    > div {
      min-width: calc(50% - 9px);
    }
  }

  @media screen and (max-width: 480px) {
    > div {
      min-width: 0 !important;
      width: 100%;
      flex: 1 1 100%;
    }
  }
`;

const StyledSimulationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  min-width: 0;
  max-width: 100%;

  @media screen and (max-width: 1279px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const StyledSimulationCard = styled.div`
  background-color: var(--white-main);
  border-radius: var(--semi-medium-border-radius);
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: var(--default-padding-small);
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  height: 100%;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .p-chip {
    font-size: var(--xsmall-font-size);
    font-weight: 400;

    svg {
      fill: none;
    }
  }

  @media screen and (max-width: 480px) {
    padding: var(--default-padding);
  }

  min-width: 0;
`;

const StyledCardContent = styled.div<{ $viewed: boolean }>`
  opacity: ${({ $viewed }) => ($viewed ? 0.5 : 1)};
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  flex: 1;
`;

const StyledTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 22px;
  width: 100%;
  flex-wrap: wrap;
  gap: var(--xsmall-padding);
`;

const StyledCalendarDate = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
  color: var(--black-main);
  margin-left: auto;
`;

const StyledCalendarIcon = styled.img`
  width: 19px;
  height: 19px;
  flex-shrink: 0;
`;

const StyledCardName = styled.h3`
  margin: 0;
  font-size: var(--medium-font-size);
  font-weight: 600;
  line-height: var(--medium-line-height);
  color: var(--black-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledPhishingSignSubtitle = styled.div`
  font-size: var(--xxsmall-font-size);
  line-height: var(--xxsmall-line-height);
  font-weight: 500;
  color: var(--black-main);
  opacity: 0.6;
`;

const StyledCardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
`;

const StyledDescription = styled.div`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--black-main);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 54px;
`;

const StyledThumbnailContainer = styled.div<{ $viewed: boolean }>`
  width: 100%;
  aspect-ratio: 571 / 351;
  border-radius: var(--xsmall-border-radius);
  overflow: hidden;
  opacity: ${({ $viewed }) => ($viewed ? 0.5 : 1)};
`;

const StyledThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const StyledBadgeRow = styled(FlexContainer)`
  gap: var(--xsmall-padding);
  justify-content: flex-end;
`;

const StyledEmptyState = styled(FlexContainer)`
  padding: var(--big-padding);
  font-size: var(--small-font-size);
  color: var(--gray-darker);
`;

const getStatusLabel = (
  status: CampaignUserResultStatusesEnum,
  t: (key: string) => string,
): string => {
  switch (status) {
    case CampaignUserResultStatusesEnum.CLICKED_LINK:
      return t('phishing.followUp.status.clickedLink');
    case CampaignUserResultStatusesEnum.SUBMITTED_DATA:
      return t('phishing.followUp.status.submittedData');
    case CampaignUserResultStatusesEnum.EMAIL_REPORTED:
      return t('phishing.followUp.status.emailReported');
    default:
      return t('phishing.followUp.status.noInteraction');
  }
};

const SimulationCard: React.FC<{
  simulation: MyPhishingSimulationSummary;
  isNewest: boolean;
}> = ({ simulation, isNewest }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const viewed = !!simulation.viewedAt;
  const labels = phishingSignsLabels(t);
  const phishingSignLabel = simulation.phishingSign
    ? labels[simulation.phishingSign as keyof typeof labels]
    : undefined;

  const handleCardClick = () => {
    navigate(
      RedirectPaths[RedirectPathsEnum.LEARNER_PHISHING_DETAIL](
        simulation.campaignId,
      ),
    );
  };

  return (
    <StyledSimulationCard
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <StyledCardContent $viewed={viewed}>
        <StyledTopRow>
          <AppChip
            label={getStatusLabel(simulation.learnerResultStatus, t)}
            type={getStatusChipType(simulation.learnerResultStatus)}
            size="xs-auto"
          />
          <StyledCalendarDate>
            <StyledCalendarIcon src={CalendarIcon} alt="" aria-hidden="true" />
            <FormatDate date={simulation.receivedAt} format="D MMM YYYY" />
          </StyledCalendarDate>
        </StyledTopRow>

        <StyledCardDetails>
          <div>
            <StyledCardName>{simulation.name}</StyledCardName>
            {phishingSignLabel && (
              <StyledPhishingSignSubtitle>
                {t('phishing.followUp.simulations.card.phishingSign')}:{' '}
                {phishingSignLabel}
              </StyledPhishingSignSubtitle>
            )}
          </div>

          <StyledDescription>
            {simulation.description ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: stripUnwantedHtmlTags(
                    simulation.description,
                    LEARNER_CONTENT_FORBIDDEN_TAGS,
                  ),
                }}
              />
            ) : (
              simulation.characteristicNames.join(', ')
            )}
          </StyledDescription>
        </StyledCardDetails>
      </StyledCardContent>

      <StyledThumbnailContainer $viewed={viewed}>
        {simulation.thumbnailUrl ? (
          <StyledThumbnail
            src={simulation.thumbnailUrl}
            alt={simulation.name}
            loading="lazy"
          />
        ) : (
          <StyledThumbnail src={CampaignThumbnailPlaceholder} alt="" />
        )}
      </StyledThumbnailContainer>

      <StyledBadgeRow align="center">
        {isNewest && (
          <AppChip
            label={t('phishing.followUp.simulations.card.latest')}
            type="error"
            size="xs-auto"
            pulsate
          />
        )}
        {viewed ? (
          <AppChip
            label={t('phishing.followUp.simulations.card.viewed')}
            type="primary"
            size="xs-auto"
            icon={<CircledCheckIcon />}
          />
        ) : (
          <AppChip
            label={t('phishing.followUp.simulations.card.notViewed')}
            type="secondary"
            size="xs-auto"
          />
        )}
      </StyledBadgeRow>
    </StyledSimulationCard>
  );
};

const toOwnRate = (count = 0, simulationsReceived = 0) =>
  Math.round((count / Math.max(simulationsReceived, 1)) * 100);

export const LearnerPhishingOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enabled: featureEnabled, account } = usePhishingFollowUpEnabled();
  const { overview, isLoading } = useMyPhishingOverview();

  useEffect(() => {
    if (account && !featureEnabled) {
      navigate(RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](), {
        replace: true,
      });
    }
  }, [account, featureEnabled, navigate]);

  if (!featureEnabled || isLoading) {
    return (
      <LearnerLayout isLoading={false}>
        <LoadingPage message={t('generic.loading')} />
      </LearnerLayout>
    );
  }

  const performance = overview?.performance;
  const simulations = overview?.simulations ?? [];
  const reportingEnabled = overview?.reportingEnabled ?? false;

  const newestCampaignId =
    simulations.length > 0 ? simulations[0].campaignId : null;

  return (
    <LearnerLayout isLoading={false}>
      <StyledBeigeContainer>
        <StyledSection>
          <StyledSectionHeader>
            <StyledSectionTitle>
              {t('phishing.followUp.performance.title')}
            </StyledSectionTitle>
            <StyledSectionDescription>
              {t('phishing.followUp.performance.description')}
            </StyledSectionDescription>
          </StyledSectionHeader>
          <StyledStatsGrid>
            <PhishingDashboardCard
              id="simulations-received"
              color="var(--black-main)"
              backgroundColor="var(--white-main)"
              borderColor="var(--gray-dark)"
              title={t('phishing.followUp.performance.simulationsReceived')}
              icon={<EmailSentIcon />}
              value={performance?.simulationsReceived ?? 0}
              description={t(
                'phishing.followUp.performance.simulationsReceived.footer',
              )}
            />
            <PhishingDashboardCard
              id="link-clicks"
              color="var(--black-main)"
              backgroundColor="var(--white-main)"
              borderColor="var(--gray-dark)"
              title={t('phishing.followUp.performance.linkClicks')}
              icon={<StatClickIcon />}
              value={performance?.linkClicks ?? 0}
              description={t('phishing.followUp.performance.averagesFooter', {
                userRate: toOwnRate(
                  performance?.linkClicks,
                  performance?.simulationsReceived,
                ),
                rate: Math.round(performance?.orgAvgClickRate ?? 0),
              })}
            />
            <PhishingDashboardCard
              id="data-submits"
              color="var(--black-main)"
              backgroundColor="var(--white-main)"
              borderColor="var(--gray-dark)"
              title={t('phishing.followUp.performance.dataSubmit')}
              icon={<StatSubmitIcon />}
              value={performance?.dataSubmits ?? 0}
              description={t('phishing.followUp.performance.averagesFooter', {
                userRate: toOwnRate(
                  performance?.dataSubmits,
                  performance?.simulationsReceived,
                ),
                rate: Math.round(performance?.orgAvgSubmitRate ?? 0),
              })}
            />
            {reportingEnabled && (
              <PhishingDashboardCard
                id="reports"
                color="var(--black-main)"
                backgroundColor="var(--white-main)"
                borderColor="var(--gray-dark)"
                title={t('phishing.followUp.performance.reported')}
                icon={<StatWarningIcon />}
                value={performance?.reports ?? 0}
                description={t('phishing.followUp.performance.averagesFooter', {
                  userRate: toOwnRate(
                    performance?.reports,
                    performance?.simulationsReceived,
                  ),
                  rate: Math.round(performance?.orgAvgReportRate ?? 0),
                })}
              />
            )}
          </StyledStatsGrid>
        </StyledSection>

        <StyledSection>
          <StyledSectionHeader>
            <StyledSectionTitle>
              {t('phishing.followUp.simulations.title')}
            </StyledSectionTitle>
            <StyledSectionDescription>
              {t('phishing.followUp.simulations.description')}
            </StyledSectionDescription>
          </StyledSectionHeader>
          {simulations.length > 0 ? (
            <StyledSimulationGrid>
              {simulations.map((simulation) => (
                <SimulationCard
                  key={simulation.campaignId}
                  simulation={simulation}
                  isNewest={simulation.campaignId === newestCampaignId}
                />
              ))}
            </StyledSimulationGrid>
          ) : (
            <StyledEmptyState justify="center" className="w-full">
              {t('phishing.followUp.simulations.empty')}
            </StyledEmptyState>
          )}
        </StyledSection>
      </StyledBeigeContainer>
    </LearnerLayout>
  );
};
